<template>
  <!-- Mobile overlay backdrop -->
  <div
    v-if="!isCollapsed && isMobile"
    class="fixed inset-0 bg-black/30 z-30 md:hidden"
    @click="toggleCollapse"
  />
  <div
    :class="[
      bgClass || 'bg-white',
      'border-r border-gray-200 flex flex-col p-3 transition-all duration-300 shrink-0 overflow-y-auto overflow-x-hidden',
      isCollapsed ? 'w-14' : (width || 'w-80'),
      !isCollapsed && isMobile ? 'fixed inset-y-0 left-0 z-40 shadow-xl' : ''
    ]"
  >
    <!-- Header -->
    <div v-if="title || showCollapseIcon" :class="['py-2.5 flex items-center justify-between border-b border-gray-200 shrink-0', isCollapsed ? 'px-1' : 'px-3']">
      <span
        v-if="!isCollapsed"
        class="text-primary-600 font-semibold text-[13px]"
      >
        {{ title }}
      </span>
      <button
        v-if="showCollapseIcon"
        type="button"
        class="p-1 btn-primary"
        :class="isCollapsed
          ? 'mx-auto'
          : ''"
        @click="toggleCollapse"
      >
        <font-awesome-icon :icon="['fas', isCollapsed ? 'chevron-right' : 'chevron-left']" class="w-4 h-4" />
      </button>
    </div>

    <!-- Navigation Menu -->
    <nav v-show="!isCollapsed" class="flex-1">
      <!-- Menu Items -->
      <template
        v-for="(item, index) in items"
        :key="item.id || index"
      >
        <div
          v-if="(item.canAccess ? canAccessFeature(item.canAccess) : true) && item.header"
          class="py-2.5 px-3 mt-2 border-b border-gray-200"
        >
          <span class="text-primary-600 font-semibold text-[13px]">{{ item.label }}</span>
        </div>
        <div
          v-else-if="item.canAccess ? canAccessFeature(item.canAccess) : true"
          class="border-b border-gray-100 last:border-b-0 py-1"
        >
          <!-- Main Menu Item -->
          <component
            :is="item.to ? 'router-link' : 'div'"
            :to="item.to"
            class="flex items-center cursor-pointer transition-colors no-underline rounded-md gap-"
            :class="[
              item.disabled
                ? 'cursor-default'
                : isItemActive(item)
                  ? 'bg-primary-100'
                  : 'hover:bg-gray-100'
            ]"
            @click="handleItemClick(item, index)"
          >
          <!-- Item Content -->
          <div class="flex-1 flex items-center gap-2 justify-between">
            <!-- Label with optional icon badge -->
            <div class="flex items-center gap-1.5 px-3 py-2">
              <font-awesome-icon
                v-if="item.icon"
                :icon="item.icon"
                :title="item.iconTitle"
                :class="item.iconClass || 'text-purple-500 text-[8px] mr-2'"
              />
              <span
                class="text-[13px] leading-tight font-medium"
                :class="[item.disabled ? 'text-gray-400' : (isItemActive(item) || expandedItems[item.id] ? 'text-gray-900' : 'text-gray-800'), { sensitive: item.sensitive }]"
                :data-spoof-kind="item.sensitive ? item.spoofKind : undefined"
              >
                {{ item.label }}
              </span>
            </div>

            <div class="flex items-center gap-1.5 pr-1">
              <!-- Text badge -->
              <span
                v-if="item.badge"
                class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                :class="item.badgeClass || 'bg-primary-100 text-primary-700'"
              >
                {{ item.badge }}
              </span>

              <!-- Expand/Collapse Icon -->
              <button
                v-if="item.children && item.children.length > 0"
                type="button"
                class="text-gray-500 text-[10px] cursor-pointer p-2"
                @click.prevent.stop="toggleItemExpansion(item)"
              >
                <font-awesome-icon
                  :icon="['fas', expandedItems[item.id] ? 'chevron-down' : 'chevron-right']"
                />
              </button>
            </div>
          </div>
        </component>

        <!-- Submenu Items -->
        <div
          v-if="expandedItems[item.id] && item.children && item.children.length > 0"
        >
          <template
            v-for="(child, childIndex) in item.children"
            :key="child.id || childIndex"
          >
            <component
              :is="child.to ? 'router-link' : 'div'"
              v-if="child.access ? canAccessFeature(child.access) : true"
              :to="child.to"
              class="px-3 py-1.5 cursor-pointer transition-colors hover:bg-gray-100 no-underline block rounded-md"
              :class="isItemActive(child) ? 'bg-primary-100' : ''"
              @click.stop="handleItemClick(child, childIndex)"
            >
              <!-- Child Label -->
              <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-1.5">
                  <font-awesome-icon
                    v-if="item.alignChildren && item.icon"
                    :icon="item.icon"
                    :class="[item.iconClass, 'invisible']"
                    aria-hidden="true"
                  />
                  <font-awesome-icon
                    v-if="child.icon"
                    :icon="child.icon"
                    :title="child.iconTitle"
                    class="w-3 h-3 mr-2"
                    :class="child.iconClass || 'text-primary-500'"
                  />
                  <span
                    class="text-[11px] leading-snug"
                    :class="isItemActive(child) ? 'text-gray-900' : 'text-gray-700'"
                  >
                    {{ child.label }}
                  </span>
                </div>
                <span
                  v-if="child.badge"
                  class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                  :class="child.badgeClass || 'bg-primary-100 text-primary-700'"
                >
                  {{ child.badge }}
                </span>
              </div>
            </component>
          </template>
        </div>
        </div>
      </template>

      <div v-if="$slots.default" class="mt-3">
        <slot />
      </div>
    </nav>

  </div>
</template>

<script>
import { canAccessFeature } from '@/utils/access';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'PageSidebar',
  components: {
    FontAwesomeIcon
  },
  data() {
    return {
      expandedItems: {},
      isCollapsed: false,
      isMobile: false
    };
  },
  props: {
    // Sidebar title
    title: {
      type: String,
      required: true
    },
    // Show collapse icon in header
    showCollapseIcon: {
      type: Boolean,
      default: true
    },
    // Custom width class (e.g. 'w-50', 'w-64'). Defaults to 'w-80'.
    width: {
      type: String,
      default: null
    },
    // Custom background class. Defaults to 'bg-gray-50'.
    bgClass: {
      type: String,
      default: null
    },
    // Collapsed state (can be controlled externally)
    collapsed: {
      type: Boolean,
      default: false
    },
    // Navigation items
    items: {
      type: Array,
      required: true,
      // Each item should have:
      // {
      //   id: unique identifier (optional)
      //   header: boolean (optional, renders as a non-clickable section header row)
      //   label: string
      //   to: route path for navigation (optional, if not provided uses click events)
      //   icon: FontAwesome icon array (optional, for collapsed state)
      //   active: boolean (optional, for static items without routes)
      //   expanded: boolean (optional)
      //   access: feature flag to check (optional)
      //   badge: string (optional, text badge displayed to the right e.g. "Primary")
      //   badgeClass: string (optional, tailwind classes for badge, defaults to primary colors)
      //   icon: FontAwesome icon array (optional, icon displayed before label e.g. ['fas', 'flask'])
      //   iconClass: string (optional, tailwind classes for icon, defaults to purple)
      //   iconTitle: string (optional, title/tooltip for the icon)
      //   children: array of child items (optional, same props supported)
      // }
    },
  },
  emits: ['item-click', 'collapse', 'expand'],
  mounted() {
    this.initializeExpandedState();
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  watch: {
    items: {
      handler() {
        this.initializeExpandedState();
      },
      deep: true
    },
    collapsed: {
      immediate: true,
      handler(newVal) {
        this.isCollapsed = this.isMobile || newVal;
      }
    }
  },
  methods: {
    canAccessFeature,
    initializeExpandedState() {
      // Initialize expanded state from items prop
      // Auto-expand parents whose child matches the current route
      const expanded = {};
      this.items.forEach(item => {
        if (item.id) {
          if (item.expanded) {
            expanded[item.id] = true;
          } else if (item.children && item.children.length > 0) {
            expanded[item.id] = item.children.some(child => this.isItemActive(child));
          } else {
            expanded[item.id] = false;
          }
        }
      });
      this.expandedItems = expanded;
    },
    isItemActive(item) {
      // If item has a route, check if it matches current route
      if (item.to) {
        // Handle both string and object route definitions
        const itemPath = typeof item.to === 'string' ? item.to : item.to.path;
        // Single-segment paths (e.g. "/", "/admin") require exact match
        const segments = itemPath.replace(/^\//, '').split('/').filter(Boolean);
        if (segments.length <= 1) return this.$route.path === itemPath;
        return this.$route.path === itemPath || this.$route.path.startsWith(itemPath + '/');
      }
      // Otherwise use the active property
      return item.active || false;
    },
    handleItemClick(item, index) {
      if (item.disabled) return;
      // If item has no route
      if (!item.to) {
        // Toggle expansion if item has children
        if (item.children && item.children.length > 0 && item.id) {
          this.expandedItems[item.id] = !this.expandedItems[item.id];
        }
        // Emit click event for items without routes
        this.$emit('item-click', item, index);
      }
      // If item has a route, router-link handles navigation
      // Expansion is only handled by chevron click for routed items
    },
    toggleItemExpansion(item) {
      // Toggle expanded state for items with children
      if (item.children && item.children.length > 0 && item.id) {
        this.expandedItems[item.id] = !this.expandedItems[item.id];
      }
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      if (this.isCollapsed) {
        this.$emit('collapse');
      } else {
        this.$emit('expand');
      }
    },
    checkMobile() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 768;
      // Auto-collapse when entering mobile
      if (this.isMobile && !wasMobile) {
        this.isCollapsed = true;
      }
    }
  }
};
</script>

<style scoped>
/* Remove default router-link styling */
a.no-underline {
  text-decoration: none;
  color: inherit;
}

a.no-underline:hover {
  color: inherit;
}

/* Smooth rotation transition */
.rotate-90 {
  transform: rotate(90deg);
}
</style>
